import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, Menu, X, Lock, Eye, EyeOff, MessageCircle, UserCircle } from 'lucide-react';
import { VaultGrowLogo } from '@/components/VaultGrowLogo';

const NAV = [
  { path: '/', label: 'Inicio' },
  { path: '/invest', label: 'Inversión' },
  { path: '/dashboard', label: 'Mi Billetera' },
  { path: '/leaderboard', label: 'Top Afiliados' },
  { path: '/withdraw', label: 'Retiros' },
];

export default function Navbar() {
  const { user, logout, API, getHeaders, SUPPORT_WHATSAPP_URL } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [showPwd, setShowPwd] = useState({ current: false, new: false, confirm: false });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwdMsg('');
    setPwdError('');

    if (pwdForm.new_password !== pwdForm.confirm_password) {
      setPwdError('La confirmación no coincide');
      return;
    }

    setPwdLoading(true);
    try {
      await axios.post(`${API}/user/change-password`, {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      }, { headers: getHeaders() });
      setPwdMsg('Contraseña actualizada');
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => {
        setChangePasswordOpen(false);
        setPwdMsg('');
      }, 600);
    } catch (err) {
      setPwdError(err.response?.data?.detail || 'No se pudo cambiar la contraseña');
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <>
      <nav style={{
        position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(13,14,13,0.85)', backdropFilter: 'blur(24px)',
        border: '1px solid rgba(158,175,172,0.12)', borderRadius: '50px',
        padding: '6px 8px 6px 16px', zIndex: 100,
        display: 'flex', alignItems: 'center', gap: '4px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)', maxWidth: '90vw',
      }}>
        <Link to="/" data-testid="navbar-logo" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '8px', textDecoration: 'none' }}>
          <VaultGrowLogo size={28} borderRadius={7} />
          <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, color: 'white', fontSize: '15px', letterSpacing: '-0.3px' }}>VaultGrow</span>
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }} className="desktop-nav">
          {NAV.map(({ path, label }) => (
            <Link key={path} to={path} data-testid={`nav-${label}`}
              className={`nav-link${location.pathname === path ? ' active' : ''}`}>
              {label}
            </Link>
          ))}
        </div>

        {user && (
          <div style={{ position: 'relative', marginLeft: '8px', paddingLeft: '12px', borderLeft: '1px solid rgba(158,175,172,0.15)' }}>
            <button
              type="button"
              onClick={() => setProfileOpen(!profileOpen)}
              style={{
                width: '32px', height: '32px', borderRadius: '50%',
                background: 'rgba(158,175,172,0.15)', border: '1px solid rgba(158,175,172,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '12px', color: '#9EAFAC', cursor: 'pointer',
              }}
            >
              {user.full_name?.charAt(0)?.toUpperCase()}
            </button>

            {profileOpen && (
              <div style={{
                position: isMobile ? 'fixed' : 'absolute',
                top: isMobile ? '82px' : '46px',
                left: isMobile ? '50%' : 'auto',
                right: isMobile ? 'auto' : 0,
                transform: isMobile ? 'translateX(-50%)' : 'none',
                width: isMobile ? 'min(360px, calc(100vw - 24px))' : '320px',
                maxWidth: isMobile ? 'calc(100vw - 24px)' : 'none',
                zIndex: 220,
                background: 'rgba(13,14,13,0.98)', border: '1px solid rgba(158,175,172,0.2)', borderRadius: '16px',
                padding: isMobile ? '14px 12px' : '14px', boxShadow: '0 12px 32px rgba(0,0,0,0.5)', backdropFilter: 'blur(18px)',
                overflow: 'hidden',
              }}>
                <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(158,175,172,0.12)' }}>
                  <p style={{ margin: 0, color: 'white', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: 1.35 }}>
                    <UserCircle size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{user.full_name}</span>
                  </p>
                  <p style={{ margin: '6px 0 0', color: '#9EAFAC', fontSize: '12px', lineHeight: 1.35, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>Celular: {user.phone_e164 || 'No disponible'}</p>
                  <p style={{ margin: '4px 0 0', color: '#9EAFAC', fontSize: '12px', lineHeight: 1.35, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>Referral: {user.referral_code || 'No disponible'}</p>
                </div>

                <button type="button" onClick={() => { setChangePasswordOpen(true); setProfileOpen(false); }} style={{
                  width: '100%', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  padding: '10px 12px', background: 'rgba(158,175,172,0.12)', border: '1px solid rgba(158,175,172,0.28)',
                  borderRadius: '10px', color: '#c9d6d3', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                }}>
                  <Lock size={13} /> Cambiar contraseña
                </button>

                <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{
                  marginTop: '10px', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  textDecoration: 'none', padding: '10px 12px', borderRadius: '10px', border: '1px solid rgba(158,175,172,0.2)',
                  background: 'rgba(158,175,172,0.05)', color: '#9EAFAC', fontSize: '13px',
                }}>
                  <MessageCircle size={13} /> Contactar a soporte
                </a>

                <button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  style={{
                    marginTop: '10px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px 12px',
                    background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.15)', borderRadius: '10px', color: '#fc8181',
                    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <LogOut size={13} /> Cerrar sesión
                </button>
              </div>
            )}
          </div>
        )}

        <button onClick={() => setOpen(!open)} style={{ display: 'none', padding: '8px', background: 'none', border: 'none', color: '#9EAFAC' }} className="mobile-toggle">
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      {open && (
        <div style={{
          position: 'fixed', top: '72px', left: '16px', right: '16px', zIndex: 99,
          background: 'rgba(13,14,13,0.97)', border: '1px solid rgba(158,175,172,0.12)',
          borderRadius: '20px', padding: '12px', backdropFilter: 'blur(20px)',
        }}>
          {NAV.map(({ path, label }) => (
            <Link key={path} to={path} onClick={() => setOpen(false)}
              style={{
                display: 'block', padding: '12px 16px', borderRadius: '12px', fontSize: '15px',
                color: location.pathname === path ? 'white' : '#9EAFAC',
                background: location.pathname === path ? 'rgba(255,255,255,0.06)' : 'transparent',
              }}>
              {label}
            </Link>
          ))}
        </div>
      )}

      {changePasswordOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 180, background: 'rgba(0,0,0,0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ width: '100%', maxWidth: '420px', background: 'rgba(13,14,13,0.98)', border: '1px solid rgba(158,175,172,0.2)', borderRadius: '16px', padding: '16px' }}>
            <h3 style={{ margin: '0 0 12px', color: 'white', fontFamily: 'Outfit, sans-serif' }}>Cambiar contraseña</h3>
            <form onSubmit={submitPassword} style={{ display: 'grid', gap: '8px' }}>
              {/* Contraseña actual */}
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd.current ? 'text' : 'password'}
                  placeholder="Contraseña actual"
                  value={pwdForm.current_password}
                  onChange={(e) => setPwdForm((prev) => ({ ...prev, current_password: e.target.value }))}
                  required
                  style={{ width: '100%', background: 'rgba(158,175,172,0.06)', border: '1px solid rgba(158,175,172,0.16)', borderRadius: '10px', padding: '10px 40px 10px 12px', color: 'white', boxSizing: 'border-box' }}
                />
                <button type="button" onClick={() => setShowPwd(p => ({ ...p, current: !p.current }))}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#586060', cursor: 'pointer', display: 'flex' }}>
                  {showPwd.current ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {/* Nueva contraseña */}
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd.new ? 'text' : 'password'}
                  placeholder="Nueva contraseña"
                  value={pwdForm.new_password}
                  onChange={(e) => setPwdForm((prev) => ({ ...prev, new_password: e.target.value }))}
                  required
                  style={{ width: '100%', background: 'rgba(158,175,172,0.06)', border: '1px solid rgba(158,175,172,0.16)', borderRadius: '10px', padding: '10px 40px 10px 12px', color: 'white', boxSizing: 'border-box' }}
                />
                <button type="button" onClick={() => setShowPwd(p => ({ ...p, new: !p.new }))}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#586060', cursor: 'pointer', display: 'flex' }}>
                  {showPwd.new ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {/* Confirmar contraseña */}
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd.confirm ? 'text' : 'password'}
                  placeholder="Confirmar nueva contraseña"
                  value={pwdForm.confirm_password}
                  onChange={(e) => setPwdForm((prev) => ({ ...prev, confirm_password: e.target.value }))}
                  required
                  style={{ width: '100%', background: 'rgba(158,175,172,0.06)', border: '1px solid rgba(158,175,172,0.16)', borderRadius: '10px', padding: '10px 40px 10px 12px', color: 'white', boxSizing: 'border-box' }}
                />
                <button type="button" onClick={() => setShowPwd(p => ({ ...p, confirm: !p.confirm }))}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#586060', cursor: 'pointer', display: 'flex' }}>
                  {showPwd.confirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {pwdError && <span style={{ color: '#f87171', fontSize: '12px' }}>{pwdError}</span>}
              {pwdMsg && <span style={{ color: '#9EAFAC', fontSize: '12px' }}>{pwdMsg}</span>}
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                <button type="submit" disabled={pwdLoading} style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  padding: '10px 12px', background: 'rgba(158,175,172,0.12)', border: '1px solid rgba(158,175,172,0.28)',
                  borderRadius: '10px', color: '#c9d6d3', fontSize: '13px', fontWeight: 700, cursor: pwdLoading ? 'default' : 'pointer',
                }}>
                  <Lock size={13} /> {pwdLoading ? 'Actualizando...' : 'Cambiar contraseña'}
                </button>
                <button type="button" onClick={() => { setChangePasswordOpen(false); setPwdError(''); setPwdMsg(''); }} style={{
                  padding: '10px 12px', background: 'transparent', border: '1px solid rgba(158,175,172,0.2)', borderRadius: '10px', color: '#9EAFAC', cursor: 'pointer',
                }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: flex !important; }
        }
      `}</style>
    </>
  );
}
